import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('fh_cart');
      if (stored) setCart(JSON.parse(stored));
    } catch (_) {}
  }, []);

  // Persist to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem('fh_cart', JSON.stringify(cart));
    } catch (_) {}
  }, [cart]);

  // Add item — uses `quantity` field to match Cart.js / Checkout.js
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i =>
          i.id === product.id
            ? { ...i, quantity: (i.quantity || 1) + 1 }
            : i
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  // Remove item by id
  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(i => i.id !== productId));
  };

  // Update quantity (removes if qty < 1)
  const updateQuantity = (productId, newQty) => {
    if (newQty < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(i => i.id === productId ? { ...i, quantity: newQty } : i)
    );
  };

  // Sum total price — price field is numeric (priceValue) or string like "₹25"
  const getCartTotal = () => {
    return cart.reduce((sum, item) => {
      const price =
        typeof item.priceValue === 'number'
          ? item.priceValue
          : typeof item.price === 'number'
          ? item.price
          : parseFloat(String(item.price).replace(/[^0-9.]/g, '')) || 0;
      return sum + price * (item.quantity || 1);
    }, 0);
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    try { localStorage.removeItem('fh_cart'); } catch (_) {}
  };

  const cartCount = cart.reduce((sum, i) => sum + (i.quantity || 1), 0);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      getCartTotal,
      clearCart,
      cartCount,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};

export default CartContext;